import { useEffect, useState } from 'react';
import {
  CANADA_BBOX,
  fetchHighRiskHotspots,
  fetchExtremeWeatherWarnings,
  fetchSmokeDetectionAnomalies,
  fetchIceStormAnomalies,
  fetchFloodAnomalies,
  fetchMediaSignalAnomalies,
} from './api';
import {
  capToQueueItem,
  hotspotToQueueItem,
  smokeToQueueItem,
  iceStormToQueueItem,
  floodToQueueItem,
  mediaSignalToQueueItem,
  type QueueItem,
} from './anomalyQueue';

/**
 * The wall's shared queue-item sweep (#269) — the same national anomaly set the
 * COP queue ranks, fetched on the wall cadence. Used by the ticker zone (#263
 * URGENT-RED takeover) and the hero's count-chip strip (#268 unacked Urgent
 * count + the Part II §5 standing-alarm line), so "unacked urgent" can never
 * disagree between wall surfaces — both read the SAME item model through the
 * SAME predicates.
 *
 * (v1's group-queue zone owned this sweep; PART X removed the queue zone from
 * the wall — queues live at desks — but the wall still needs the items for
 * urgency surfaces. Read-only, like every wall data path.)
 */

/** Wall cadence — refresh the queue picture every 30s (the wall standard). */
export const WALL_QUEUE_REFRESH_MS = 30_000;

export function useWallQueueItems(refreshMs: number = WALL_QUEUE_REFRESH_MS): QueueItem[] {
  const [items, setItems] = useState<QueueItem[]>([]);

  useEffect(() => {
    let cancelled = false;
    const ctl = new AbortController();

    const refresh = async (): Promise<void> => {
      try {
        const [hotspots, cap, smoke, ice, flood, media] = await Promise.all([
          fetchHighRiskHotspots(CANADA_BBOX, ctl.signal, { minConfidence: 60 }),
          fetchExtremeWeatherWarnings(CANADA_BBOX, ctl.signal),
          fetchSmokeDetectionAnomalies(CANADA_BBOX, ctl.signal),
          fetchIceStormAnomalies(CANADA_BBOX, ctl.signal),
          fetchFloodAnomalies(CANADA_BBOX, ctl.signal),
          fetchMediaSignalAnomalies(CANADA_BBOX, ctl.signal),
        ]);
        if (cancelled) return;
        setItems([
          ...hotspots.features.map(hotspotToQueueItem),
          ...cap.features.map(capToQueueItem),
          ...smoke.features.map(smokeToQueueItem),
          ...ice.features.map(iceStormToQueueItem),
          ...flood.features.map(floodToQueueItem),
          ...media.features.map(mediaSignalToQueueItem),
        ]);
      } catch (err) {
        if ((err as { name?: string }).name !== 'AbortError') {
          console.error('useWallQueueItems refresh failed', err);
        }
      }
    };

    void refresh();
    const timer = setInterval(() => void refresh(), refreshMs);
    return () => {
      cancelled = true;
      ctl.abort();
      clearInterval(timer);
    };
  }, [refreshMs]);

  return items;
}
